/**
 * Display formatters.
 *
 * D-9: money is integer cents in the database and is only ever turned into a
 * string here. Acreage is numeric(10,3). D-8: timestamps are stored UTC and
 * displayed in America/New_York.
 */

export const DISPLAY_TZ = "America/New_York";

/** Cents → `$45,000` (no cents shown unless they are non-zero). */
export function formatMoneyCents(cents: number | null | undefined): string {
  if (cents === null || cents === undefined || Number.isNaN(cents)) return "—";
  const dollars = cents / 100;
  const hasFraction = cents % 100 !== 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: hasFraction ? 2 : 0,
  }).format(dollars);
}

/** Cents → `$45.0k` / `$1.2M`, for KPI tiles where width is scarce. */
export function formatMoneyCompact(cents: number | null | undefined): string {
  if (cents === null || cents === undefined || Number.isNaN(cents)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    // currency style defaults minimumFractionDigits to 2, which would render $45.0K
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(cents / 100);
}

/** numeric(10,3) acres → `12.5 ac`, trailing zeros trimmed. */
export function formatAcres(acres: number | string | null | undefined): string {
  if (acres === null || acres === undefined || acres === "") return "—";
  const n = typeof acres === "string" ? Number(acres) : acres;
  if (Number.isNaN(n)) return "—";
  const trimmed = n.toFixed(3).replace(/\.?0+$/, "");
  return `${trimmed} ac`;
}

/** E.164 → `(555) 123-4567`; anything else is returned unchanged. */
export function formatPhone(e164: string | null | undefined): string {
  if (!e164) return "—";
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  if (m) return `(${m[1]}) ${m[2]}-${m[3]}`;
  return e164;
}

/** Last 4 only — for anywhere a full number should not be shown. */
export function maskPhone(e164: string | null | undefined): string {
  if (!e164) return "—";
  const digits = e164.replace(/\D/g, "");
  if (digits.length < 4) return "—";
  return `***${digits.slice(-4)}`;
}

/** UTC ISO → `Sep 25, 2026` in America/New_York. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: DISPLAY_TZ,
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(d);
}

/** UTC ISO → `Sep 25, 2026, 10:31 AM` in America/New_York. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: DISPLAY_TZ,
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
}

/**
 * `in 3d` / `2h overdue` / `now`, relative to `now`.
 * Overdue is the case the Overview page is built around (§7.2), so it reads explicitly.
 */
export function formatDue(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const ms = d.getTime() - now.getTime();
  const abs = Math.abs(ms);
  const mins = Math.round(abs / 60000);
  if (mins < 1) return "now";
  const unit = mins < 60 ? `${mins}m` : abs < 86400000 ? `${Math.round(mins / 60)}h` : `${Math.round(mins / 1440)}d`;
  return ms < 0 ? `${unit} overdue` : `in ${unit}`;
}

/**
 * `5d ago` / `3h ago` / `just now` — a timestamp in the PAST that is not a deadline (v2.1 T5.5).
 * "Last touched" used `formatDue`, which reads any past instant as "5d overdue": a chip that said a
 * dead lead had work outstanding.
 */
export function formatAgo(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const mins = Math.round(Math.max(0, now.getTime() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
}

/** `0.42` → `42%`. */
export function formatPct(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return `${(value * 100).toFixed(digits)}%`;
}

/** Tier → the colour role used for its badge. */
export function tierTone(tier: string | null | undefined): "ok" | "info" | "warn" | "muted" {
  switch (tier) {
    case "A":
      return "ok";
    case "B":
      return "info";
    case "C":
      return "warn";
    default:
      return "muted";
  }
}

/**
 * Placeholder values the database has to store but nobody should read.
 *
 * `parcels.county` and `parcels.state` are `not null` because they are part of the D-7 identity key, so
 * a parcel whose county SmarterContact never sent is stored as "Unknown" / "??". Printing that on a lead
 * says the county IS Unknown, which is a claim about a place that does not exist — and it read as a
 * market on §7.8's "Where the leads came from", where one bar was labelled with a word that appears
 * nowhere in the business. An em dash says "we do not know", which is the truth.
 */
const SENTINELS = new Set(["unknown", "??", "?", "n/a", "na", "none", "null", "undefined", "-"]);

/** Any value that is absent, blank, or a stored placeholder → `—`. */
export function shown(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const text = String(value).trim();
  if (!text || SENTINELS.has(text.toLowerCase())) return "—";
  return text;
}

/**
 * `public.lead_action_is_actionable` in TypeScript (v2.1 T5.4, D-35): the ingest placeholder is not
 * work while its suggestion is still pending. Kept in step with the SQL by `format.test.ts`.
 */
export const PLACEHOLDER_ACTION = "Triage new lead";
export function isActionable(
  nextAction: string | null | undefined,
  suggestionState: string | null | undefined,
): boolean {
  // The lead row calls a pending suggestion "queued"; the column's own value is "pending".
  const pending = suggestionState === "pending" || suggestionState === "queued";
  return nextAction !== PLACEHOLDER_ACTION || !pending;
}
